/**
 * Everything that scopes the board, in one row above everything it scopes.
 *
 * All three controls narrow the same tree, so they sit together and the numbers below always
 * agree with what is set here. The row is sticky because the tree is long once a page or two is
 * open: a filter you have to scroll back up to change is a filter people stop using.
 */
import { Search, X } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { formatDay } from './chartTokens';

/** Presets before a custom range: nobody fights a calendar grid for "last 30 days". */
export const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

/** The value the area Select uses for "no area filter" — Radix will not take an empty string. */
export const ALL_AREAS = 'all';

export function FilterBar({
  days,
  onDaysChange,
  area,
  onAreaChange,
  areas,
  query,
  onQueryChange,
  range,
  resultCount,
  totalCount,
}: {
  days: number;
  onDaysChange: (days: number) => void;
  area: string;
  onAreaChange: (area: string) => void;
  /** Bold sidebar headings that hold a page here, in sidebar order, with their page counts. */
  areas: { name: string; parent: string | null; pages: number }[];
  query: string;
  onQueryChange: (query: string) => void;
  range: { from: string; to: string };
  resultCount: number;
  totalCount: number;
}) {
  const filtered = area !== ALL_AREAS || query.trim() !== '';

  return (
    <div className="sticky top-0 z-10 -mx-1 space-y-2 border-b border-zinc-200 bg-zinc-50/95 px-1 py-3 backdrop-blur">
      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((preset) => (
          <Button
            key={preset.days}
            size="sm"
            // Selection is carried by the variant rather than a hand-rolled class pair, so the
            // chosen range still reads as chosen if the design system restyles buttons.
            variant={days === preset.days ? 'primary' : 'outline'}
            onClick={() => onDaysChange(preset.days)}
          >
            {preset.label}
          </Button>
        ))}

        {/*
          The options are the SIDEBAR's own bold headings, groups and subgroups alike, because that
          is how anybody here describes where a page lives. Only the ones that hold a page in this
          range are listed — an option that leads to an empty list is a promise the board cannot
          keep — and the count says how much is behind each, so a one-page section reads as one
          page rather than as a filter that broke.
        */}
        <div className="ml-1 w-56">
          <Select value={area} onValueChange={onAreaChange}>
            <SelectTrigger size="md" aria-label="Filter by section">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_AREAS}>All sections</SelectItem>
              {areas.map((heading) => (
                <SelectItem key={heading.name} value={heading.name}>
                  {/* Indented like the sidebar indents it, so the nesting is visible in a flat list. */}
                  {heading.parent ? `\u00a0\u00a0${heading.name}` : heading.name} ({heading.pages})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/*
          A plain labelled input rather than the design system's floating-label field: this one
          carries an icon, a clear button and no label of its own, and the floating label would
          sit on top of the placeholder that explains what can be searched.
        */}
        <div className="relative min-w-[13rem] flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search a page, tab or feature…"
            aria-label="Search pages, tabs and features"
            className="h-10 w-full rounded-lg border border-zinc-300 bg-white pr-8 pl-9 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-400 focus:outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              aria-label="Clear search"
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:text-zinc-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <span className="text-xs whitespace-nowrap text-zinc-400">
          {formatDay(range.from)} – {formatDay(range.to)}
        </span>
      </div>

      {/* Only when something is actually filtered: a permanent "12 of 12" is noise that teaches
          people to stop reading the line, which is the line that says when pages are hidden. */}
      {filtered && (
        <p className="text-xs text-zinc-500">
          {resultCount === 0
            ? 'No page matches those filters.'
            : `Showing ${resultCount} of ${totalCount} pages.`}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-zinc-700"
            onClick={() => {
              onAreaChange(ALL_AREAS);
              onQueryChange('');
            }}
          >
            Clear filters
          </button>
        </p>
      )}
    </div>
  );
}
